import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@components/Button";
import { ButtonType } from "@components/Button/types";
import { Row } from "@components/Flex";
import { Link } from "@components/Link";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Switch } from "@components/Switch";
import { Body, Caption, H2 } from "@components/Typography";
import { Backdrop, Modal } from "@contexts/ModalContext/styles";
import { useEditGoal, useErrorBanner, useGoalsContext, useProjectedChart } from "@hooks";
import { Translations, i18n } from "@i18n";
import { calculateProjectedAmount, convertDollarToCents, convertToMultiplier } from "@utils";
import dayjs from "dayjs";
import { validationSchema } from "./content";
import { ListItem } from "./ListItem";
import { useStyles } from "./styles";
import { ProjectionCalculationFormValues } from "./types";

export const ProjectionCalculationModal = () => {
  const styles = useStyles();

  const [showMore, setShowMore] = useState(false);

  const {
    isInfoVisible,
    setIsInfoVisible,
    projectedAge,
    setProjectedAge,
    multiplier,
    setMultiplier,
    contributeAfter18,
    setContributeAfter18,
    initialBalance,
    recurringAmount,
    recurringDepositType,
  } = useProjectedChart();

  const [contributionSelection, setContributionSelection] = useState(contributeAfter18);

  const { goalId, goalLengthInYears, interest } = useGoalsContext();
  const [editGoal, { loading }] = useEditGoal();

  const { showErrorBanner } = useErrorBanner();

  const defaultPercentage = Math.round((multiplier - 1) * 100);

  const {
    register,
    watch,
    setValue,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<ProjectionCalculationFormValues>({
    defaultValues: {
      age: projectedAge,
      percentage: defaultPercentage,
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (goalId) {
      setValue("age", goalLengthInYears);
      setValue("percentage", interest);
      setContributionSelection(contributeAfter18);
    }
  }, [goalId, goalLengthInYears, interest, contributeAfter18]);

  const ageController = register("age");
  const percentageController = register("percentage");

  const handleDiscard = () => {
    setIsInfoVisible(false);
  };

  const renderBackdrop = (props: any) => <Backdrop {...props} onClick={handleDiscard} />;

  const handleUpdate = async ({ age, percentage }: ProjectionCalculationFormValues) => {
    const newMultiplier = convertToMultiplier(percentage);

    setMultiplier(newMultiplier);
    setProjectedAge(age);
    setContributeAfter18(contributionSelection);

    if (goalId) {
      const targetYear = dayjs().year() + age;

      const newProjectedAmount = calculateProjectedAmount({
        amount: recurringAmount,
        contributeAfter18: contributionSelection,
        currentAge: 0,
        projectedAge: age,
        recurringDepositType,
        initialBalance,
        multiplier: newMultiplier,
      });

      try {
        await editGoal({
          variables: {
            goalId,
            contributionsAfter18: contributionSelection,
            projectedRateOfReturn: percentage,
            targetYear,
            targetAmountInCents: convertDollarToCents(newProjectedAmount),
          },
        });
      } catch {
        showErrorBanner();
      }
    }

    setIsInfoVisible(false);
  };

  return (
    <Modal show={isInfoVisible} renderBackdrop={renderBackdrop}>
      <div style={styles.container}>
        <H2>{i18n.t(Translations.ProjectionInfoTitle)}</H2>
        <Spacing size={SpacingSizes.XL} />

        <ListItem
          label={i18n.t(Translations.ProjectionInfoRateOfReturn)}
          error={errors.percentage?.message}
          right={
            <Row>
              <input
                {...percentageController}
                type="text"
                value={watch("percentage")}
                style={styles.input}
              />
              <Body>%</Body>
            </Row>
          }
        />
        <Spacing size={SpacingSizes.M} />

        <ListItem
          label={i18n.t(Translations.ProjectionInfoGoalLengthInYears)}
          error={errors.age?.message}
          right={
            <input
              {...ageController}
              type="text"
              value={watch("age").toString()}
              style={styles.input}
            />
          }
        />
        <Spacing size={SpacingSizes.M} />

        <ListItem
          label={i18n.t(Translations.ProjectionInfoContributeAfter18)}
          right={
            <Switch
              onChange={() => setContributionSelection(!contributionSelection)}
              checked={contributionSelection}
            />
          }
        />
        <Spacing size={SpacingSizes.M} />

        <div style={styles.disclaimer}>
          <Caption>{i18n.t(Translations.ProjectionInfoDisclaimer1)}</Caption>
          <Spacing size={SpacingSizes.M} />

          {showMore ? (
            <>
              <Caption>{i18n.t(Translations.ProjectionInfoDisclaimer2)}</Caption>
              <Spacing size={SpacingSizes.M} />

              <Caption>{i18n.t(Translations.ProjectionInfoDisclaimer3)}</Caption>
              <Spacing size={SpacingSizes.M} />
            </>
          ) : (
            <Link
              text={i18n.t(Translations.ProjectionInfoReadMore)}
              onClick={() => setShowMore(true)}
              style={styles.link}
            />
          )}
        </div>
        <Spacing size={SpacingSizes.M} />

        <Row>
          <Button
            title={i18n.t(Translations.ProjectionInfoSecondaryCTA)}
            type={ButtonType.Secondary}
            onClick={handleDiscard}
            buttonStyle={styles.secondary}
          />
          <div style={styles.divider} />
          <Button
            title={i18n.t(Translations.ProjectionInfoPrimaryCTA)}
            disabled={!isValid}
            loading={loading}
            onClick={handleFormSubmit(handleUpdate)}
          />
        </Row>
      </div>
    </Modal>
  );
};
